import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { useQuery } from "@tanstack/react-query";
import { useChosenEmployer } from "../../contexts/EmployerContext";
import HttpService from "../../services/HttpService";
import { getWidgetComponent } from "./widgets";
import SupersetEmbeddedDashboard from "../../components/SupersetEmbeddedDashboard";

/**
 * WidgetCard - Delegator component that fetches data and delegates rendering
 * to specialized widget components based on widget type
 */
export default function WidgetCard({
  widget,
  timeRange,
  isMaximized,
  onToggleMaximize,
}) {
  const { chosenEmployerId } = useChosenEmployer();

  // Calculate date range
  const getDateRange = (range) => {
    const endDate = new Date();
    const startDate = new Date();

    switch (range) {
      case "today":
        startDate.setDate(endDate.getDate());
        break;
      case "yesterday":
        startDate.setDate(endDate.getDate() - 1);
        break;
      case "7days":
        startDate.setDate(endDate.getDate() - 7);
        break;
      case "30days":
        startDate.setDate(endDate.getDate() - 30);
        break;
      case "90days":
        startDate.setDate(endDate.getDate() - 90);
        break;
      case "ytd":
        startDate.setMonth(0, 1);
        break;
      default:
        startDate.setDate(endDate.getDate() - 30);
    }

    // Add 1 day to endDate to include the current date in the range (if backend uses < endDate)
    endDate.setDate(endDate.getDate() + 1);

    // Format using local time to avoid UTC shifting
    const formatDate = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate),
    };
  };

  // Fetch widget data
  async function fetchWidgetData() {
    const { startDate, endDate } = getDateRange(timeRange);
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/dashboard/widgets/${widget.widgetCode}/data`,
      {
        params: { startDate, endDate },
      }
    );
    return response;
  }

  const {
    data: widgetDataResponse,
    isLoading: isLoadingData,
    isFetching,
    isError,
    refetch,
    dataUpdatedAt,
  } = useQuery(["widgetData", widget.widgetCode, timeRange], fetchWidgetData, {
    enabled: !!chosenEmployerId && !!widget.widgetCode,
    refetchOnWindowFocus: false,
    staleTime: 60 * 60 * 1000, // Consider data fresh for 60 minutes
  });

  const data = widgetDataResponse?.data || [];

  // Get the appropriate widget component from registry
  const WidgetComponent = getWidgetComponent(widget.widgetType);

  // Parse metadata from widget definition (e.g. custom grid sizes)
  let metadata = {};
  if (widget.metadata) {
    try {
      metadata = JSON.parse(widget.metadata);
    } catch (e) {
      console.warn("Failed to parse widget metadata", widget.widgetCode, e);
    }
  }

  // Dynamic sizing based on maximized state
  // Use widget's configured grid sizes, with fallback to defaults, and metadata overrides
  const gridSize = isMaximized
    ? { xs: 12, md: 12, lg: 12 }
    : {
        xs: metadata.grid?.xs || widget.defaultGridXs || 12,
        md: metadata.grid?.md || widget.defaultGridMd || 6,
        lg: metadata.grid?.lg || widget.defaultGridLg || 3,
      };
  const chartHeight = isMaximized ? 500 : metadata.height || 300;

  const handleDownloadCSV = () => {
    if (!data || data.length === 0) return;

    // 1. Define Headers
    const headers = [
      "Date",
      "Sort Order",
      "Series",
      "Label",
      "Value (Y)",
      "Value (Z)",
    ];

    // 2. Map Data to Rows
    const rows = data.map((item) => [
      item.dateRef,
      item.sortOrder || "",
      item.seriesName ? `"${item.seriesName.replace(/"/g, '""')}"` : "", // Escape quotes
      `"${item.xLabel.replace(/"/g, '""')}"`,
      item.yValue,
      item.zValue || "",
    ]);

    // 3. Construct CSV Content
    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.join(",")),
    ].join("\n");

    // 4. Trigger Download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${widget.title.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0, 10)}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  return (
    <Grid item {...gridSize} sx={{ transition: "all 0.3s ease-in-out" }}>
      <Card
        sx={{
          backgroundColor: "transparent",
          borderRadius: widget.widgetType === "CARD" ? "20px" : "8px",
        }}
        variant="elevation"
        elevation={3}
      >
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Tooltip title={widget.title}>
              <Typography
                variant="h6"
                noWrap
                sx={{
                  flex: 1,
                  minWidth: 0,
                  mr: 1,
                  opacity: widget.widgetType === "CARD" ? 0.5 : 1,
                  fontSize: widget.widgetType === "CARD" ? "1rem" : "1.4rem",
                  fontWeight: 700,
                }}
              >
                {widget.title}
              </Typography>
            </Tooltip>
            <Box>
              {!metadata.hideDownload && widget.widgetType !== "CARD" && (
                <Tooltip title="Download CSV">
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleDownloadCSV}
                      sx={{ ml: 1 }}
                      disabled={!data || data.length === 0 || isLoadingData}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {!metadata.hideRefresh && (
                <Tooltip
                  title={
                    dataUpdatedAt
                      ? `Last refreshed: ${new Date(dataUpdatedAt).toLocaleTimeString()}`
                      : "Refresh Data"
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => refetch()}
                      sx={{ ml: 1 }}
                      disabled={isFetching}
                    >
                      {isFetching ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <RefreshIcon />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {widget.widgetType !== "CARD" && (
                <IconButton
                  size="small"
                  onClick={onToggleMaximize}
                  sx={{ ml: 1 }}
                >
                  {isMaximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              )}
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {widget.description}
          </Typography>
          <Box mt={2}>
            {isLoadingData ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : isError ? (
              <Alert severity="error">Failed to load widget data</Alert>
            ) : widget.widgetType.startsWith("SUPERSET") &&
              window.SUPERSET_URL ? (
              <SupersetEmbeddedDashboard
                dashboardId={widget.metadata}
                height="500px"
              />
            ) : data.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No data available
              </Typography>
            ) : WidgetComponent ? (
              <WidgetComponent
                data={data}
                config={widget}
                height={chartHeight}
              />
            ) : (
              <Alert severity="warning">
                Widget type "{widget.widgetType}" is not yet implemented
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
}
